import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ProductionQuantityLimitsOutlinedIcon from '@mui/icons-material/ProductionQuantityLimitsOutlined';
import TaxiAlertOutlined from '@mui/icons-material/TaxiAlertOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { useCountTime } from 'custom-hooks/useCountTime';
import {
  useReadyAtStoreAlarm,
  useDeliveredAlarm,
} from 'custom-hooks/useOrderAlert';
import dateParser from 'helpers/dateParser';
import { ORDER_STATUS_LABEL_MAPPING } from 'lib/orderStatuses';
import getPlatformLogo from 'lib/platformLogo';
import HeaderListData from 'views/commons/HeaderListData';

import CustomerCell from './CustomerCell';
import OrderAddress from './OrderAddress';
import OrderDelivery from './OrderDelivery';
import OrderFilesMenu from './OrderFilesMenu';
import OrderStatus from './OrderStatus';

function renderWmsErrorDetails(parsedMessage) {
  if (!parsedMessage) return null;

  const {
    mensaje,
    skusNoEncontrados = [],
    skusStockInsuficiente = [],
  } = parsedMessage;

  return (
    <Box>
      <Typography variant="body2" gutterBottom>
        {mensaje}
      </Typography>

      {skusNoEncontrados.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            SKUs no encontrados:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {skusNoEncontrados.map((sku) => (
              <li key={sku}>
                <Typography variant="caption">{sku}</Typography>
              </li>
            ))}
          </ul>
        </Box>
      )}

      {skusStockInsuficiente.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            SKUs con stock insuficiente:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {skusStockInsuficiente.map(({ sku, requerido, disponible }) => (
              <li key={sku}>
                <Typography variant="caption">
                  {sku} - Requerido: {requerido}, Disponible: {disponible}
                </Typography>
              </li>
            ))}
          </ul>
        </Box>
      )}
    </Box>
  );
}

const columns = ({
  hasProcessingTime,
  overrideWmsStatus,
  wmsStatusOverrides,
}) => [
  {
    Header: <HeaderListData fontSize="0.8rem">#Orden</HeaderListData>,
    accessor: 'sourceOrderId',
    disableSortBy: true,
    // eslint-disable-next-line react/prop-types
    Cell: ({ value, row: { original } }) => {
      const {
        // eslint-disable-next-line react/prop-types
        wmsId,
        // eslint-disable-next-line react/prop-types
        warehouse: { name: warehouseName },
        // eslint-disable-next-line react/prop-types
        seller: { name: sellerName },
        // eslint-disable-next-line react/prop-types
        wmsSystemName,
        // eslint-disable-next-line react/prop-types
        fixedSourceOrderId,
      } = original;

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" sx={{ color: 'inherit' }}>
            {value}
          </Typography>
          <Typography variant="caption">
            {wmsSystemName || 'WMS'} ID:{' '}
            {wmsSystemName === 'FLEXY' ? fixedSourceOrderId : wmsId}
          </Typography>
          <Typography variant="caption">{warehouseName}</Typography>
          <Typography variant="caption">{sellerName}</Typography>
        </Box>
      );
    },
  },
  {
    Header: <HeaderListData fontSize="0.8rem">Plataforma</HeaderListData>,
    id: 'platform',
    // eslint-disable-next-line react/prop-types
    Cell: ({
      // eslint-disable-next-line react/prop-types
      row: {
        // eslint-disable-next-line react/prop-types
        original: {
          // eslint-disable-next-line react/prop-types
          system: { id },
        },
      },
    }) => (
      <Box
        component="img"
        sx={{
          width: 80,
          height: 'auto',
        }}
        alt={id}
        src={getPlatformLogo(id)}
      />
    ),
  },
  {
    Header: <HeaderListData fontSize="0.8rem">Estado WMS</HeaderListData>,
    id: 'wmsStatus',
    Cell: ({
      // eslint-disable-next-line react/prop-types
      row: {
        // eslint-disable-next-line react/prop-types
        original: { id, wmsStatus: originalWmsStatus, wmsErrorMessage },
      },
    }) => {
      const wmsStatus = wmsStatusOverrides?.[id] || originalWmsStatus;

      let parsedMessage;
      try {
        parsedMessage = wmsErrorMessage ? JSON.parse(wmsErrorMessage) : null;
      } catch (e) {
        parsedMessage = null;
      }

      return (
        <Box sx={{ maxWidth: 150, flexShrink: 1 }}>
          <Typography variant="caption">{wmsStatus || 'PENDIENTE'}</Typography>
          {wmsStatus === 'ERROR' && parsedMessage && (
            <Accordion
              sx={{ backgroundColor: '#fff3f3', position: 'relative' }}
              elevation={0}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography
                  variant="caption"
                  color="error"
                  noWrap
                  sx={{ maxWidth: 120 }}
                >
                  {parsedMessage?.mensaje || 'Error en WMS'}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    position: 'absolute',
                    backgroundColor: 'white',
                    boxShadow: 4,
                    borderRadius: 2,
                    zIndex: 10,
                    p: 2,
                    mt: 1,
                    minWidth: 300,
                    maxWidth: '80vw',
                    overflowX: 'auto',
                    right: 0,
                  }}
                >
                  {renderWmsErrorDetails(parsedMessage)}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      );
    },
  },
  {
    Header: <HeaderListData fontSize="0.8rem">Fecha ingreso</HeaderListData>,
    accessor: 'createdAt',
    Cell: ({
      // eslint-disable-next-line react/prop-types
      value,
      // eslint-disable-next-line react/prop-types
      row: {
        // eslint-disable-next-line react/prop-types
        original: {
          // eslint-disable-next-line react/prop-types
          delivery: { shippingMethod },
          // eslint-disable-next-line react/prop-types
          status,
        },
      },
    }) => {
      const { formattedDistance: processingTime, minutesDifference } =
        useCountTime(value);

      const readyAtStoreAlarm = useReadyAtStoreAlarm({
        createdAt: value,
        minutesDifference,
        status,
        shippingMethod,
      });
      const deliveredAlarm = useDeliveredAlarm({
        createdAt: value,
        minutesDifference,
        shippingMethod,
      });
      const color = readyAtStoreAlarm?.color || deliveredAlarm?.color;
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography noWrap variant="caption">
            {dateParser(value)}
          </Typography>
          {hasProcessingTime && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                noWrap
                variant="caption"
                color={(theme) => theme.palette[color]?.main}
              >
                <b>{processingTime}</b>
              </Typography>
              {readyAtStoreAlarm.show && (
                <Tooltip title="Preparación atrasada">
                  <ProductionQuantityLimitsOutlinedIcon
                    color={readyAtStoreAlarm.color}
                  />
                </Tooltip>
              )}
              {deliveredAlarm.show && (
                <Tooltip title="Entrega atrasada">
                  <TaxiAlertOutlined color={deliveredAlarm.color} />
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      );
    },
  },
  {
    Header: <HeaderListData fontSize="0.8rem">Tipo</HeaderListData>,
    accessor: 'businessType',
    disableSortBy: true,
    Cell: ({
      // eslint-disable-next-line react/prop-types
      row: {
        // eslint-disable-next-line react/prop-types
        original: {
          // eslint-disable-next-line react/prop-types
          businessType,
        },
      },
    }) => (
      <Box sx={{ display: 'flex', alignContent: 'center', gap: 1 }}>
        <Chip label={businessType} size="small" />
      </Box>
    ),
  },
  {
    Header: <HeaderListData fontSize="0.8rem">Estado</HeaderListData>,
    accessor: 'status',
    disableSortBy: true,
    // eslint-disable-next-line react/prop-types
    Cell: ({
      // eslint-disable-next-line react/prop-types
      value: { value },
      // eslint-disable-next-line react/prop-types
      row: {
        // eslint-disable-next-line react/prop-types
        original: { id, statusOptions, invoiceURL, reasonCanceled },
      },
    }) => (
      <OrderStatus
        id={id}
        value={value}
        label={ORDER_STATUS_LABEL_MAPPING[value]}
        options={statusOptions}
        invoiceURL={invoiceURL}
        reasonCanceled={reasonCanceled}
      />
    ),
  },
  {
    Header: <HeaderListData fontSize="0.8rem">Cliente</HeaderListData>,
    accessor: 'customer',
    disableSortBy: true,
    // eslint-disable-next-line react/prop-types
    Cell: ({ value, row }) => (
      // eslint-disable-next-line react/prop-types
      <CustomerCell customer={value} delivery={row.original.delivery} />
    ),
  },
  {
    Header: <HeaderListData fontSize="0.8rem">Delivery</HeaderListData>,
    accessor: 'delivery',
    disableSortBy: true,
    // eslint-disable-next-line react/prop-types
    Cell: ({
      // eslint-disable-next-line react/prop-types
      value: { shippingMethod, systemName, status },
      // eslint-disable-next-line react/prop-types
      row: {
        // eslint-disable-next-line react/prop-types
        original: {
          // eslint-disable-next-line react/prop-types
          id: orderId,
          // eslint-disable-next-line react/prop-types
          system: { id: systemId },
          // eslint-disable-next-line react/prop-types
          seller: { id: sellerId },
          // eslint-disable-next-line react/prop-types
          status: orderStatus,
        },
      },
    }) => (
      <OrderDelivery
        id={orderId}
        shippingMethod={shippingMethod}
        systemName={systemName}
        systemId={systemId}
        status={status}
        sellerId={sellerId}
        orderStatus={orderStatus}
      />
    ),
  },
  {
    id: 'address',
    Header: (
      <HeaderListData align="center" fontSize="0.8rem">
        Dirección
      </HeaderListData>
    ),
    accessor: 'delivery',
    disableSortBy: true,
    // eslint-disable-next-line react/prop-types
    Cell: ({
      // eslint-disable-next-line react/prop-types
      value: {
        // eslint-disable-next-line react/prop-types
        dropOffAddress,
      },
      // eslint-disable-next-line react/prop-types
      row: {
        // eslint-disable-next-line react/prop-types
        original: {
          // eslint-disable-next-line react/prop-types
          id,
          // eslint-disable-next-line react/prop-types
          system: { id: platformName },
          // eslint-disable-next-line react/prop-types
          sourceOrderId,
          // eslint-disable-next-line react/prop-types
          status: { value: statusValue },
        },
      },
    }) => (
      <OrderAddress
        id={id}
        systemName={platformName}
        sourceOrderId={sourceOrderId}
        dropOffAddress={dropOffAddress}
        status={statusValue}
      />
    ),
  },
  {
    id: 'actions',
    disableSortBy: true,
    // eslint-disable-next-line react/prop-types
    Cell: ({ row: { original } }) => {
      const {
        // eslint-disable-next-line react/prop-types
        sourceOrderId,
        // eslint-disable-next-line react/prop-types
        invoiceURL,
        // eslint-disable-next-line react/prop-types
        pickingList,
        // eslint-disable-next-line react/prop-types
        delivery: { shippingLabelURL },
        // eslint-disable-next-line react/prop-types
        packages: { labelURL },
        // eslint-disable-next-line react/prop-types
        id,
        // eslint-disable-next-line react/prop-types
        attachedFiles,
      } = original;
      return (
        <OrderFilesMenu
          id={id}
          sourceOrderId={sourceOrderId}
          // eslint-disable-next-line react/prop-types
          hasAttachedFiles={!!attachedFiles?.length}
          invoiceURL={invoiceURL}
          packagesLabelURL={labelURL}
          // eslint-disable-next-line react/prop-types
          hasPickingList={!!pickingList?.length}
          shippingLabelURL={shippingLabelURL}
          overrideWmsStatus={overrideWmsStatus}
          wmsStatusOverrides={wmsStatusOverrides}
        />
      );
    },
  },
];
export default columns;
